/**
 * Platform seam for native/web social sign-in (ELARA-728).
 *
 * `signInWithPopup` is browser-only — it crashes on React Native, which has no popups. The OAuth dance
 * therefore diverges per platform: web opens a popup; React Native runs the native Google / Apple
 * account UI and exchanges the result for a Firebase credential. The shared `SessionState` owns
 * neither path; it delegates to this injected port so the stores stay platform-neutral, exactly like
 * the auth-persistence (ELARA-689) and fetch (ELARA-688) seams.
 *
 * Apple is gated behind {@link SocialAuthPort.isAppleAvailable}: App Store guideline 4.8 requires Sign
 * in with Apple wherever a third-party social login (Google) is offered, but the button must not be
 * shown where it cannot work (web, Android), so the screens read this flag to decide.
 */
import type { UserCredential } from 'firebase/auth';

/** Which social provider a sign-in attempt targeted — for telemetry and provider-specific messaging. */
export type SocialProvider = 'google' | 'apple';

/**
 * Raised by a platform adapter when a provider credential does not belong to an existing Elara
 * account. Firebase normally creates a new Auth user during a federated "sign in"; mobile rejects
 * and removes that user because membership signup is web-only.
 */
export const SOCIAL_ACCOUNT_NOT_FOUND_ERROR_CODE = 'auth/social-account-not-found';

/**
 * Outcome of a social sign-in attempt.
 *
 * Cancellation is modelled as a first-class state rather than thrown as an error: the user dismissing
 * the Google/Apple sheet is a normal interaction, not a failure, and the calling store must NOT paint
 * an error banner for it. Genuine failures (network, misconfiguration, denied consent) still throw.
 */
export type SocialSignInOutcome =
  | { status: 'signed-in'; credential: UserCredential }
  | { status: 'cancelled' };

export interface SocialAuthPort {
  /**
   * Whether Sign in with Apple can run on this platform. `true` only on iOS native; `false` on web
   * and Android. The auth screens render the Apple button only when this is `true`.
   */
  readonly isAppleAvailable: boolean;
  /** Runs the platform Google OAuth flow and exchanges the result for a Firebase session. */
  signInWithGoogle(): Promise<SocialSignInOutcome>;
  /**
   * Runs the platform Apple OAuth flow and exchanges the result for a Firebase session. Throws if the
   * platform does not support it (callers should gate on {@link isAppleAvailable}).
   */
  signInWithApple(): Promise<SocialSignInOutcome>;
}
